/**
 * 브레드크럼 네비게이션 컴포넌트
 *
 * 현재 URL 경로를 기반으로 계층 구조 탐색 경로를 표시한다.
 * - react-router-dom의 useLocation으로 현재 경로 감지
 * - 경로별 한국어/영어 라벨 매핑
 * - 홈 페이지에서는 표시하지 않음
 * - AntD Breadcrumb 대신 직접 렌더링 (CSS 충돌 방지)
 */
import React from 'react';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';

// ─── 경로 라벨 매핑 ──────────────────────────────────────────
const ROUTE_LABELS = {
  ko: {
    prd: '농약',
    pesticides: '농약정보',
    mrls: 'MRL',
    foods: '식품',
    adi: 'ADI',
    using: '사용',
    analysis: '분석',
    'analysis-l': '분석(구)',
    reports: '보고서',
    proposals: '안전성보고서',

    vd: '동물약품',
    drugs: '약품정보',

    std: '기준규격',
    standards: '기준',
    applications: '신청',
    requests: '요청',
    companies: '업체',
    statuses: '상태',
    units: '단위',

    doc: '게시판',
    articles: '공지사항',
    related: '관련사이트',

    admin: '관리자',
    dashboard: '대시보드',
    users: '사용자',
    'auth-templates': '권한템플릿',
    visitors: '방문자',
    audit: '감사이력',
    workflow: '워크플로우',
    notifications: '알림',
    batch: '배치작업',
    quality: '데이터품질',
    popups: '팝업관리',

    search: '검색결과',
  },
  en: {
    prd: 'Pesticides',
    pesticides: 'Pesticide Info',
    mrls: 'MRL',
    foods: 'Food',
    adi: 'ADI',
    using: 'Usage',
    analysis: 'Analysis',
    'analysis-l': 'Analysis (Legacy)',
    reports: 'Reports',
    proposals: 'Safety Reports',

    vd: 'Veterinary Drugs',
    drugs: 'Drug Info',

    std: 'Standards',
    standards: 'Standards',
    applications: 'Applications',
    requests: 'Requests',
    companies: 'Companies',
    statuses: 'Status',
    units: 'Units',

    doc: 'Board',
    articles: 'Articles',
    related: 'Related Sites',

    admin: 'Admin',
    dashboard: 'Dashboard',
    users: 'Users',
    'auth-templates': 'Auth Templates',
    visitors: 'Visitors',
    audit: 'Audit Log',
    workflow: 'Workflow',
    notifications: 'Notifications',
    batch: 'Batch Jobs',
    quality: 'Data Quality',
    popups: 'Popup Mgmt',

    search: 'Search Results',
  },
};

// ─── 섹션별 부모 경로 매핑 ──────────────────────────────────
const SECTION_PARENTS = {
  prd: '/prd/pesticides',
  vd: '/vd/drugs',
  std: '/std/standards',
  doc: '/doc/prd-board',
  admin: '/admin/dashboard',
};

// ─── 인라인 스타일 ──────────────────────────────────
const styles = {
  nav: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    fontSize: 14,
    padding: '8px 0',
    marginBottom: 12,
    whiteSpace: 'nowrap',
  },
  link: {
    color: '#999',
    textDecoration: 'none',
    fontSize: 14,
  },
  separator: {
    color: '#ccc',
    margin: '0 6px',
    fontSize: 14,
  },
  current: {
    color: '#555',
    fontWeight: 500,
    fontSize: 14,
  },
};

/**
 * 앱 브레드크럼
 */
const AppBreadcrumb = () => {
  const location = useLocation();
  const { language } = useLanguage();

  const labels = ROUTE_LABELS[language] || ROUTE_LABELS.ko;

  // 홈 페이지에서는 표시 안 함
  if (location.pathname === '/') {
    return null;
  }

  // 경로 세그먼트 분리 (예: '/prd/mrls' → ['prd', 'mrls'])
  const segments = location.pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const items = [];

  // 홈 아이콘
  items.push(
    <Link key="home" to="/" style={styles.link}>
      <HomeOutlined />
    </Link>
  );

  // 경로 세그먼트들
  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const label = labels[segment] || segment;

    // separator
    items.push(
      <span key={`sep-${index}`} style={styles.separator}>/</span>
    );

    if (isLast) {
      // 마지막 세그먼트는 텍스트만
      items.push(
        <span key={`seg-${index}`} style={styles.current}>{label}</span>
      );
    } else if (index === 0) {
      // 섹션은 부모 경로로 링크
      const parentPath = SECTION_PARENTS[segment];
      items.push(
        parentPath
          ? <Link key={`seg-${index}`} to={parentPath} style={styles.link}>{label}</Link>
          : <span key={`seg-${index}`} style={styles.link}>{label}</span>
      );
    } else {
      items.push(
        <span key={`seg-${index}`} style={styles.link}>{label}</span>
      );
    }
  });

  return (
    <nav style={styles.nav}>
      {items}
    </nav>
  );
};

export default AppBreadcrumb;
